/**
 * System administration → Agents. Two views over one application's agent
 * playbooks: the MONITOR (what is running, waiting, stuck; how much of each
 * persona's credit budget is spent; every run with its step-by-step detail)
 * and PERSONAS (the service accounts, their profiles and assignments).
 *
 * Host-agnostic: the platform Run view and a downloaded runtime mount this
 * same component and pass an `AgentsRuntime`.
 */
import { useCallback, useEffect, useState } from 'react'
import type { AgentsRuntime, MonitorSummary, PlaybookRun, RunState } from './contract'
import { C, Empty, cardStyle, formatWhen, labelStyle } from '../jobs/ui'
import { BudgetBar, RunStateChip, credits, smallBtn } from './bits'
import PersonasView from './PersonasView'
import RunDetail from './RunDetail'

const TILES: { state: RunState | 'waiting'; label: string; tone: string }[] = [
  { state: 'running', label: 'Running', tone: C.accent },
  { state: 'waiting', label: 'Waiting', tone: C.warn },
  { state: 'queued', label: 'Queued', tone: C.info },
  { state: 'suspended', label: 'Suspended', tone: C.err },
  { state: 'paused', label: 'Paused', tone: C.ink2 },
  { state: 'completed', label: 'Completed', tone: C.ok },
  { state: 'failed', label: 'Failed', tone: C.err },
]

const FILTERS: { key: string; label: string; states: string }[] = [
  { key: 'all', label: 'All', states: '' },
  { key: 'live', label: 'In progress', states: 'queued,running,waiting_human,waiting_event' },
  { key: 'stopped', label: 'Paused / suspended', states: 'paused,suspended' },
  { key: 'done', label: 'Finished', states: 'completed,failed,cancelled' },
]

function RunRow({ run, onOpen }: { run: PlaybookRun; onOpen(): void }) {
  return (
    <button type="button" onClick={onOpen} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
      border: 'none', borderTop: `1px solid ${C.line}`, background: 'transparent', color: C.ink,
      cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap',
    }}>
      <span style={{ fontWeight: 600, fontSize: '0.9125rem', flex: '1 1 160px', minWidth: 0 }}>{run.playbook}</span>
      <RunStateChip state={run.state} label={run.state_label} />
      <span style={{ fontSize: '0.8125rem', color: C.ink2 }}>{formatWhen(run.started_at || run.queued_at)}</span>
      <span style={{ fontSize: '0.8125rem', color: C.ink2, minWidth: 70, textAlign: 'right' }}>
        {credits(run.credits_spent)} cr
      </span>
    </button>
  )
}

function Monitor({ runtime, onOpenRun }: { runtime: AgentsRuntime; onOpenRun(id: string): void }) {
  const [summary, setSummary] = useState<MonitorSummary | null>(null)
  const [runs, setRuns] = useState<PlaybookRun[]>([])
  const [filter, setFilter] = useState('all')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const states = FILTERS.find((f) => f.key === filter)?.states || ''
      const [s, r] = await Promise.all([runtime.monitor(), runtime.listRuns({ state: states, limit: 100 })])
      setSummary(s)
      setRuns(r)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the monitor.')
    }
  }, [runtime, filter])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const id = window.setInterval(() => { if (!document.hidden) void load() }, 5000)
    return () => window.clearInterval(id)
  }, [load])

  if (error) return <div style={{ color: C.err }}>{error}</div>
  if (!summary) return <div style={{ color: C.ink2 }}>Loading…</div>

  const count = (s: string) => s === 'waiting'
    ? (summary.by_state.waiting_human || 0) + (summary.by_state.waiting_event || 0)
    : summary.by_state[s as RunState] || 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: C.ink }}>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
        {TILES.map((t) => (
          <div key={t.state} style={{ ...cardStyle, padding: '10px 12px' }}>
            <div style={labelStyle}>{t.label}</div>
            <div style={{ fontSize: '1.4625rem', fontWeight: 700, color: count(t.state) ? t.tone : C.faint }}>
              {count(t.state)}
            </div>
          </div>
        ))}
      </div>

      {summary.attention.length > 0 && (
        <div>
          <div style={{ ...labelStyle, marginBottom: 6, color: C.err }}>Needs attention</div>
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            {summary.attention.map((r) => (
              <div key={r.run_id}>
                <RunRow run={r} onOpen={() => onOpenRun(r.run_id)} />
                {r.error_text && (
                  <div style={{ padding: '0 12px 8px', fontSize: '0.8425rem', color: C.err }}>{r.error_text}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.waiting.length > 0 && (
        <div>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Waiting on people or events</div>
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            {summary.waiting.map((r) => <RunRow key={r.run_id} run={r} onOpen={() => onOpenRun(r.run_id)} />)}
          </div>
        </div>
      )}

      {summary.budgets.length > 0 && (
        <div>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Credit budgets this cycle</div>
          <div style={{ ...cardStyle, padding: '4px 12px' }}>
            {summary.budgets.map((b, i) => (
              <div key={b.assign_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
                borderTop: i ? `1px solid ${C.line}` : 'none', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 180px', fontSize: '0.9125rem' }}>
                  <b>{b.persona_name || b.persona_id}</b> · {b.playbook}
                </span>
                <BudgetBar spent={b.cycle_spent} budget={b.budget_credits} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ ...labelStyle, flex: 1 }}>Runs</div>
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              style={{ ...smallBtn(filter === f.key), height: 26 }}>{f.label}</button>
          ))}
        </div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {runs.length === 0
            ? <Empty title="No runs" hint="Runs appear here once a persona's playbook starts — on its cycle, from Run now, or from a scheduler." />
            : runs.map((r) => <RunRow key={r.run_id} run={r} onOpen={() => onOpenRun(r.run_id)} />)}
        </div>
      </div>
    </div>
  )
}

export default function AgentsPanel({ runtime }: { runtime: AgentsRuntime }) {
  const [view, setView] = useState<'monitor' | 'personas'>('monitor')
  const [runId, setRunId] = useState('')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${C.line}`, paddingBottom: 8 }}>
        {(['monitor', 'personas'] as const).map((v) => (
          <button key={v} type="button" onClick={() => { setView(v); setRunId('') }}
            style={{ ...smallBtn(view === v), height: 28 }}>
            {v === 'monitor' ? 'Monitor' : 'Personas'}
          </button>
        ))}
      </div>
      {view === 'monitor' && (runId
        ? <RunDetail runtime={runtime} runId={runId} onBack={() => setRunId('')} />
        : <Monitor runtime={runtime} onOpenRun={setRunId} />)}
      {view === 'personas' && <PersonasView runtime={runtime} />}
    </div>
  )
}
